// Global Instructions Rule Applied!
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import type { RootState } from '../../../state_manager/store.ts'
import { archiveNameFor, createZip } from '../../../core/utils/zip.ts'
import type { ZipEntry } from '../../../core/utils/zip.ts'
import { fetchReportPdf, saveBlob } from '../services/documentReportsService.ts'

/** One value of the fan-out parameter — a fund id and the name to show. */
export interface BatchTarget {
  value: string
  label: string
}

export interface DownloadProgress {
  running: boolean
  /** Reports finished (successfully or not). */
  completed: number
  total: number
  /** Name of the report being generated right now. */
  currentLabel: string | null
}

export interface DownloadOutcome {
  success: boolean
  filename?: string
  /** Targets that failed, by label, when some still succeeded. */
  failed?: string[]
  error?: string
}

interface RunArgs {
  reportCode: string
  params: Record<string, unknown>
  /** Parameter to vary; when absent the report is generated once, as-is. */
  batchParamKey?: string | null
  targets: BatchTarget[]
}

const IDLE: DownloadProgress = { running: false, completed: 0, total: 0, currentLabel: null }

/**
 * Generates a report for each selected value and delivers the results.
 *
 * One request per report rather than one request for all of them: a full
 * portfolio run is well over a minute of rendering, which a gateway idle
 * timeout would cut off, and a single request can report no progress. Going
 * one at a time also means a fund that fails is named and skipped instead of
 * losing the whole batch.
 *
 * The requests are sequential by design — the backend renders under a single
 * lock and one browser, so overlapping them would queue on the server while
 * making the progress count meaningless.
 */
export function useDocumentReportDownload() {
  // Read here rather than having the caller pass it, matching
  // useDocumentReportPreview — the token is the hook's business, not the
  // screen's, and one source for it means one place to get it wrong.
  const accessToken = useSelector((state: RootState) => state.auth?.accessToken)
  const [progress, setProgress] = useState<DownloadProgress>(IDLE)

  // Set by cancel(), read between reports. A run is abandoned at the next
  // boundary rather than mid-render, so the server is never left with an
  // orphaned target.
  const cancelled = useRef(false)
  // Guards against setting state after the screen has gone away mid-run.
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const cancel = useCallback(() => {
    cancelled.current = true
  }, [])

  const run = useCallback(
    async (args: RunArgs): Promise<DownloadOutcome> => {
      const { reportCode, params, batchParamKey, targets } = args
      const single = !batchParamKey || targets.length <= 1

      cancelled.current = false
      setProgress({
        running: true,
        completed: 0,
        total: Math.max(targets.length, 1),
        currentLabel: targets[0]?.label ?? null
      })

      try {
        const collected: ZipEntry[] = []
        const filenames: string[] = []
        const failed: string[] = []

        for (const [index, target] of targets.entries()) {
          if (cancelled.current) break
          if (mounted.current) {
            setProgress((prev) => ({ ...prev, completed: index, currentLabel: target.label }))
          }

          // 'export': the PDF carries external links only (company websites),
          // never in-app URLs — the recipient has no app session to open them.
          const perReport = batchParamKey
            ? { ...params, [batchParamKey]: target.value, link_mode: 'export' }
            : { ...params, link_mode: 'export' }
          const result = await fetchReportPdf(
            { reportCode, params: perReport },
            accessToken ?? null
          )

          if (result.success && result.blob && result.filename) {
            collected.push({
              name: result.filename,
              bytes: new Uint8Array(await result.blob.arrayBuffer())
            })
            filenames.push(result.filename)
          } else {
            failed.push(target.label)
          }
        }

        if (cancelled.current) {
          return { success: false, error: 'Download cancelled' }
        }
        if (collected.length === 0) {
          return {
            success: false,
            error: failed.length === 1
              ? `${failed[0]} failed to generate`
              : `All ${failed.length} reports failed to generate`
          }
        }

        if (single && collected.length === 1) {
          // A lone report is a plain PDF — no one wants to unzip one file.
          const only = collected[0]
          saveBlob(new Blob([only.bytes], { type: 'application/pdf' }), only.name)
          return { success: true, filename: only.name }
        }

        const archiveName = archiveNameFor(filenames, reportCode)
        saveBlob(createZip(collected), archiveName)
        return {
          success: true,
          filename: archiveName,
          failed: failed.length > 0 ? failed : undefined
        }
      } catch (e) {
        return { success: false, error: e instanceof Error ? e.message : 'Download failed' }
      } finally {
        if (mounted.current) setProgress(IDLE)
      }
    },
    // Rebuilt when the token is refreshed, so a long run started after a
    // refresh never sends the token it closed over at mount.
    [accessToken]
  )

  return { progress, run, cancel }
}
